import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex } from "antd";
import { ColumnsType } from "antd/es/table";

import SText from "@/styles/SText";
import { Price } from "@/types/sales";
import dayjs from "dayjs";
import { dateFormat, priceFormat } from "@/utils/format";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

export type UsePriceColumn = (props: {
  onDetail: (params: Price | null) => void;
  onDel: (id: string) => void;
}) => ColumnsType<Price>

const usePriceColumn: UsePriceColumn = ({ onDetail, onDel }) => {
  const { t } = useTranslation();

  const columns: ColumnsType<Price> = useMemo(() => [
    {
      title: <SText text={t("product.code")} />,
      key: "code",
      width: 80,
      fixed: "left",
      render: (_, item) => (
        <SText key={item.id + "code"} text={item.product?.code ?? ""} />
      ),
    },
    {
      title: <SText text={t("product.name")} />,
      key: "name",
      width: 120,
      fixed: "left",
      render: (_, item) => (
        <SText key={item.id + "name"} text={item.product?.name ?? ""} />
      ),
    },
    {
      title: <SText text={t("product.brand1")} />,
      key: "model1",
      width: 180,
      render: (_, item) => <SText text={item.product?.model?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("product.brand2")} />,
      key: "model2",
      width: 180,
      render: (_, item) => <SText text={item.product?.t1?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("sale.taxPrice")} />,
      key: "price",
      width: 80,
      render: (_, item) => <SText text={priceFormat(item.price ?? "")} />,
    },
    {
      title: <SText text={t("sale.estimateDays")} />,
      key: "estimateDays",
      width: 80,
      render: (_, item) => (
        <SText key={item.id + "estimateDays"} text={item.estimateDays?.toString() ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.expiredAt")} />,
      key: "expiredAt",
      width: 80,
      render: (_, item) => (
        <SText key={item.id + "expiredAt"} text={item.expiredAt ? dateFormat(dayjs(item.expiredAt)) : ''} />
      ),
    },
    {
      title: <SText text={t("sale.customerModel")} />,
      key: "customerModel",
      width: 80,
      render: (_, item) => (
        <SText key={item.id + "customerModel"} text={item.customerModel ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.mfgr")} />,
      key: "mfgr",
      width: 80,
      render: (_, item) => (
        <SText key={item.id + "mfgr"} text={item.mfgr ?? ""} />
      ),
    },
    {
      title: <SText text={t("common.desc")} />,
      key: "desc",
      width: 280,
      render: (_, item) => <SText text={item.desc ?? ""} style={{ width: '100%' }} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      key: "uom",
      width: 60,
      render: (_, item) => <SText text={item.product?.uom?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: t("common.opt"),
      key: "action",
      fixed: 'right',
      width: 60,
      render: (_, item) => (
        <Flex>
          <Button type="link" onClick={() => onDetail(item)}>
            {t("common.detail")}
          </Button>
          {/* 隐藏删除 */}
          {/* <Button type="link" danger onClick={() => onDel(item.id)}>
            {t("common.del")}
          </Button> */}
        </Flex>
      ),
    },
  ], [t]);

  return columns;
}

export default usePriceColumn;